import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { msalInstance } from './config/authConfig';
import App from './App';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Initialize MSAL and wait for it to complete before rendering
// This ensures MSAL is ready to handle redirects when the app loads
msalInstance.initialize()
  .then(async () => {
    console.log('[Main] MSAL initialized successfully');
    console.log('[Main] MSAL redirect URI:', msalInstance.getConfiguration().auth.redirectUri);
    console.log('[Main] MSAL client ID:', msalInstance.getConfiguration().auth.clientId);
    
    // Check if we have a redirect response in the URL
    const hash = window.location.hash;
    const search = window.location.search;
    const hasAuthResponse = hash.includes('code=') || search.includes('code=') || hash.includes('error=');
    
    if (hasAuthResponse) {
      console.log('[Main] ⚠️ Auth response detected - processing redirect');
      console.log('[Main] Current URL:', window.location.href);
      console.log('[Main] Current origin + pathname:', window.location.origin + window.location.pathname);
      const configuredRedirectUri = msalInstance.getConfiguration().auth.redirectUri;
      console.log('[Main] Configured redirect URI:', configuredRedirectUri);
      
      // CRITICAL: Compare redirect URIs to diagnose mismatch
      const currentUrlBase = window.location.origin + window.location.pathname;
      const normalizedCurrent = currentUrlBase.replace(/\/$/, '');
      const normalizedConfigured = configuredRedirectUri?.replace(/\/$/, '') || '';
      console.log('[Main] Redirect URI match check:');
      console.log('[Main]   Current (normalized):', normalizedCurrent);
      console.log('[Main]   Configured (normalized):', normalizedConfigured);
      console.log('[Main]   Match:', normalizedCurrent === normalizedConfigured);
      
      // CRITICAL: Explicitly handle redirect promise BEFORE rendering
      // This ensures MSAL can match the state and process the auth code
      
      // Debug: Check MSAL state in localStorage before processing
      try {
        const msalStateKeys = Object.keys(localStorage).filter(key => 
          key.startsWith('msal.')
        );
        console.log('[Main] Total MSAL keys in localStorage:', msalStateKeys.length);
        
        // Check for redirect state specifically
        const redirectStateKeys = msalStateKeys.filter(key => 
          key.includes('interaction.status') || key.includes('Redirect')
        );
        console.log('[Main] Redirect-related MSAL keys:', redirectStateKeys.length);
        if (redirectStateKeys.length > 0 && process.env.NODE_ENV === 'development') {
          console.log('[Main] Redirect state keys (dev):', redirectStateKeys);
        }
      } catch (e) {
        console.warn('[Main] Could not check localStorage:', e);
      }
      
      // Debug: Extract state from URL
      try {
        const hashParams = new URLSearchParams(hash.substring(1));
        const stateFromUrl = hashParams.get('state');
        if (stateFromUrl) {
          console.log('[Main] State parameter present in URL');
          try {
            // State is URL encoded, decode it first
            const decodedStateParam = decodeURIComponent(stateFromUrl);
            const decodedState = JSON.parse(atob(decodedStateParam));
            console.log('[Main] Decoded state ID:', decodedState.id);
            console.log('[Main] Decoded state meta:', decodedState.meta);
          } catch (e) {
            console.log('[Main] Could not decode state - format might be different');
          }
        } else {
          console.warn('[Main] ⚠️ No state parameter found in URL!');
        }
      } catch (e) {
        console.warn('[Main] Could not parse hash params:', e);
      }
      
      // IMPORTANT: Clear any stale interaction state before processing redirect
      // This can help if there's a previous failed redirect attempt
      try {
        const allMsalKeys = Object.keys(localStorage).filter(key => key.startsWith('msal.'));
        const staleInteractionKeys = allMsalKeys.filter(key => 
          (key.includes('interaction.status') || key.includes('Redirect')) &&
          !key.includes(normalizedCurrent.replace(/[^a-zA-Z0-9]/g, ''))
        );
        if (staleInteractionKeys.length > 0) {
          console.log('[Main] Clearing stale interaction state keys:', staleInteractionKeys.length);
          // Don't actually clear - MSAL might need them
          // Instead, just log them for debugging
        }
      } catch (e) {
        // Ignore
      }
      
      try {
        // CRITICAL: Call handleRedirectPromise with explicit error handling
        console.log('[Main] Calling handleRedirectPromise()...');
        console.log('[Main] Current URL base for validation:', currentUrlBase);
        console.log('[Main] Configured redirect URI:', configuredRedirectUri);
        const redirectResponse = await msalInstance.handleRedirectPromise();
        
        if (redirectResponse) {
          console.log('[Main] ✅ Redirect handled successfully');
          console.log('[Main] Account:', redirectResponse.account?.username);
          
          // Clean up URL hash after successful authentication
          if (window.location.hash) {
            window.history.replaceState({}, '', window.location.pathname + window.location.search);
          }
        } else {
          console.error('[Main] ❌ handleRedirectPromise returned null - redirect failed!');
          console.error('[Main] This indicates MSAL could not match the redirect response.');
          console.error('[Main] Possible causes:');
          console.error('[Main]   1. Redirect URI mismatch - stored state has different redirect URI');
          console.error('[Main]   2. State parameter mismatch - state in URL doesn\'t match stored state');
          console.error('[Main]   3. Redirect was already processed - state was cleared');
          console.error('[Main]   4. State expired or was cleared by browser/localStorage');
          
          // Check if accounts exist despite failed redirect
          const accounts = msalInstance.getAllAccounts();
          console.error('[Main] Accounts in cache after failed redirect:', accounts.length);
          
          // Detailed debugging: Check all MSAL cache entries
          try {
            const allMsalKeys = Object.keys(localStorage).filter(key => key.startsWith('msal.'));
            console.error('[Main] All MSAL cache keys:', allMsalKeys.length);
            
            // Try to find the interaction state
            const interactionKeys = allMsalKeys.filter(key => 
              key.includes('interaction') || key.includes('Redirect') || key.includes('state')
            );
            if (interactionKeys.length > 0) {
              console.error('[Main] Interaction-related keys found:', interactionKeys);
            } else {
              console.error('[Main] ⚠️ No interaction state found in cache - state may have been cleared!');
            }
          } catch (e) {
            console.warn('[Main] Could not inspect MSAL cache:', e);
          }
          
          // Try to extract error from URL
          try {
            const hashParams = new URLSearchParams(hash.substring(1));
            const error = hashParams.get('error');
            const errorDescription = hashParams.get('error_description');
            if (error) {
              console.error('[Main] ❌ Auth error in URL:', error, errorDescription);
            } else {
              console.error('[Main] No error in URL - this suggests a state/redirect URI mismatch');
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
      } catch (error) {
        console.error('[Main] ❌ Error handling redirect:', error);
        // Try to extract error from URL
        try {
          const hashParams = new URLSearchParams(hash.substring(1));
          const urlError = hashParams.get('error');
          const errorDescription = hashParams.get('error_description');
          if (urlError) {
            console.error('[Main] ❌ Auth error in URL:', urlError, errorDescription);
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
    } else {
      // No redirect response, but still call handleRedirectPromise to clean up any stale state
      try {
        await msalInstance.handleRedirectPromise();
      } catch (error) {
        // Ignore errors when there's no redirect to handle
        console.debug('[Main] No redirect to handle:', error);
      }
    }
    
    // Render the app after MSAL is initialized and redirect is processed
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <App />
            {process.env.NODE_ENV === 'development' && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
          </QueryClientProvider>
        </MsalProvider>
      </React.StrictMode>
    );
  })
  .catch((error) => {
    console.error('[Main] ❌ Error initializing MSAL:', error);
    console.error('[Main] Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    
    // Still render the app even if MSAL initialization fails (user can see error)
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1>Authentication Error</h1>
          <p>Failed to initialize authentication. Please refresh the page.</p>
          <p style={{ fontSize: '12px', color: '#666' }}>{error.message}</p>
        </div>
      </React.StrictMode>
    );
  });


